import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadPerformance,
  clearPerformance,
} from "../../actions/performanceAction";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import TimeRange from "@marenaud/react-timeline-range-slider";
import "swiper/swiper-bundle.css";
import moment from "moment";
import _ from "lodash";
import { ResponsiveContainer } from "recharts";
import { useNavigate } from "react-router-dom";
import PerformanceImage from "./components/PerformanceImage";
import Export from "../../layouts/Export";
import CircularProgress from "@mui/material/CircularProgress";
import { CardContent, Card, Tabs, Tab, Chip } from "@mui/material";
import StackedLineChartOutlinedIcon from "@mui/icons-material/StackedLineChartOutlined";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import AddchartOutlinedIcon from "@mui/icons-material/AddchartOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import { motion } from "framer-motion";
import SpaceUtilization from "./components/SpaceUtilizationKPI";
import OccupancyUtilization from "./components/OccupancyUtilizationKPI";
import EntryExit from "./components/EntryExitKPI";
import KPIIndicator from "../../layouts/KPIIndicator";
import PositiveNegativeBarChart from "../../layouts/PositiveNegativeBarChart";
import { HeatmapPlotlyChart } from "../../layouts/HeatmapPlotlyChart";
import PeakOccupancyKPI from "./components/PeakOccupancyKPI";
import Top5KPI from "./components/Top5KPI";
import NoOfEmptySeatKPI from "./components/NoOfEmptySeatKPI";
import NoOfPeopleKPI from "./components/NoOfPeopleKPI";
import NoOfPeopleChart from "./components/NoOfPeopleChart";
import NoOfEmptySeatChart from "./components/NoOfEmptySeatChart";
import SpaceUtilizationChart from "./components/SpaceUtilizationChart";
import OccupancyUtilizationChart from "./components/OccupancyUtilizationChart";
import PanZoomController from "../../layouts/PanZoomController";
import ZoomController from "../../layouts/ZoomController";
import CustomTooltip from "../../layouts/CustomTooltip";

// your forceUpdate hook
const useForceUpdate = () => {
  const [value, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update the state to force render
};

// Style for the Carousel
const cardStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "200px",
  height: "200px",
  backgroundColor: "#072545",
  border: "none",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
};

const chipStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#d3f4fe",
  minWidth: "50px",
  gap: "4px",
  fontSize: "15px",
  fontWeight: 500,
};

const cardContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#FFFFFF",
};

const breakpoints = {
  // when window width is >= 768px
  768: {
    slidesPerView: 3,
    spaceBetween: 20,
  },
  // when window width is >= 992px
  992: {
    slidesPerView: 4,
    spaceBetween: 30,
  },
  // when window width is >= 1200px
  1200: {
    slidesPerView: 6,
    spaceBetween: 40,
  },
  // when window width is >= 1400px
  1400: {
    slidesPerView: 7,
    spaceBetween: 50,
  },
};

const Performance = () => {
  // KPI threshold
  const peoplePer100sqft = 2;

  let navigate = useNavigate();
  const forceUpdate = useForceUpdate();
  const dispatch = useDispatch();

  const [date, setDate] = useState([
    moment().subtract(5, "days"),
    moment(), // 5 days ago, current time
  ]);

  const [selectedTime, setSelectedTime] = useState([
    moment().subtract(5, "days"),
    moment(), // 5 days ago, current time
  ]);

  const [maxDate, setMaxDate] = useState(moment().startOf("day"));
  const [minDate, setMinDate] = useState(moment().subtract(5, "days"));

  const [error, setError] = useState(false);
  const [room, setRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedAllRooms, setSelectedAllRooms] = useState(null);
  const [modalChart, setModalChart] = useState(null);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const dark = useSelector((state) => state.dark.data);
  const performance = useSelector((state) => state.performance.data);
  const performanceLoading = useSelector((state) => state.performance.loading);
  const dashboard = useSelector((state) => state.dashboard.data);
  const building = useSelector((state) => state.building.data);
  const floor = useSelector((state) => state.floor.data);
  const userInfo = useSelector((state) => state.auth.user);
  const [filteredPerformance, setFilteredPerformance] = useState(null);

  // State of the Tabs
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // State of patten selected
  const [selectedPattern, setSelectedPattern] = useState(4);

  // State of the Swiper
  const [swiper, setSwiper] = useState(null);
  const swiperRef = useRef(null);
  const panZoomRef = useRef(null);

  const [tooltipRoom, setTooltipRoom] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showDetail, setShowDetail] = useState(false);

  const handleZoomIn = () => {
    if (panZoomRef.current) {
      panZoomRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (panZoomRef.current) {
      panZoomRef.current.zoomOut();
    }
  };

  const handleReset = () => {
    if (panZoomRef.current) {
      panZoomRef.current.reset();
    }
  };

  // Change between group KPI tabs
  const handleTabClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
    }

    setSelectedTabIndex(index);
  };

  // Function: Format data for export feature
  const format = (report, type = 1) => {
    let arr = [];

    // Avg Entry Exit Report
    if (type === 1) {
      const localTimeZone = moment.tz.guess();

      for (let i = 0; i < report?.length; i++) {
        const utcFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .format("YYYY-MM-DD HH:mm:ss");

        const localTimeFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .tz(localTimeZone)
          .format("YYYY-MM-DD HH:mm:ss");

        arr.push({
          Room: report[i]?.["room.Room"],
          Entry: report[i]?.Entry,
          Exit: report[i]?.Exit,
          "Net Entry Exit": Math.abs(
            (report[i]?.Exit ?? 0) - (report[i]?.Entry ?? 0)
          ), 
          // Timestamp: new Date(report[i].enqueuedTime_Stamp).toISOString(),
          "UTC Time (+00:00)": utcFormatted,
          "Local Time": localTimeFormatted,
        });
      }
    }
    // Occupancy Report
    else if (type === 2) {
      for (let i = 0; i < report?.length; i++) {
        const localTimeZone = moment.tz.guess();

        const utcFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .format("YYYY-MM-DD HH:mm:ss");

        const localTimeFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .tz(localTimeZone)
          .format("YYYY-MM-DD HH:mm:ss");

        arr.push({
          Room: report[i]?.room?.Room,
          "Robot Facing": report[i]?.room?.camera_Direction,
          "Office(s)": [
            report[i]?.room?.front_Desc
              ? `Front(${report[i].room.front_Desc})`
              : null,
            report[i]?.room?.right_Desc
              ? `Right(${report[i].room.right_Desc})`
              : null,
            report[i]?.room?.back_Desc
              ? `Back(${report[i].room.back_Desc})`
              : null,
            report[i]?.room?.left_Desc
              ? `Left(${report[i].room.left_Desc})`
              : null,
          ]
            .filter(Boolean)
            .join(", "),
          "Number of Persons(Front)": report[i]?.NoP_front
            ? report[i]?.NoP_front
            : 0,
          "Number of Persons(Right)": report[i]?.NoP_right
            ? report[i]?.NoP_right
            : 0,
          "Number of Persons(Back)": report[i]?.NoP_back
            ? report[i]?.NoP_back
            : 0,
          "Number of Persons(Left)": report[i]?.NoP_left
            ? report[i]?.NoP_left
            : 0,
          "Number of Persons(Total)":
            report[i]?.NoP_right +
            report[i]?.NoP_front +
            report[i]?.NoP_back +
            report[i]?.NoP_left,
          "Number of Empty Seats(Front)": report[i]?.NoES_front
            ? report[i]?.NoES_front
            : 0,
          "Number of Empty Seats(Right)": report[i]?.NoES_right
            ? report[i]?.NoES_right
            : 0,
          "Number of Empty Seats(Back)": report[i]?.NoES_back
            ? report[i]?.NoES_back
            : 0,
          "Number of Empty Seats(Left)": report[i]?.NoES_left
            ? report[i]?.NoES_left
            : 0,
          "Number of Empty Seats(Total)":
            report[i]?.NoES_right +
            report[i]?.NoES_front +
            report[i]?.NoES_back +
            report[i]?.NoES_left,
          "UTC Time (+00:00)": utcFormatted,
          "Local Time": localTimeFormatted,
        });
      }
    }
    // Net Entry Exit Report
    else if (type === 3) {
      const localTimeZone = moment.tz.guess();

      for (let i = 0; i < report?.length; i++) {
        const utcFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .format("YYYY-MM-DD HH:mm:ss");

        const localTimeFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .tz(localTimeZone)
          .format("YYYY-MM-DD HH:mm:ss");

        arr.push({
          Room: report[i]?.["room.Room"],
          "Net Entry Exit": Math.abs(
            (report[i]?.Exit ?? 0) - (report[i]?.Entry ?? 0)
          ),
          "UTC Time (+00:00)": utcFormatted,
          "Local Time": localTimeFormatted,
        });
      }
    }
    // Entry Exit Activity Report
    else if(type ===4){
 const localTimeZone = moment.tz.guess();

      for (let i = 0; i < report?.length; i++) {
        const utcFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .format("YYYY-MM-DD HH:mm:ss");

        const localTimeFormatted = moment
          .utc(report[i].enqueuedTime_Stamp)
          .tz(localTimeZone)
          .format("YYYY-MM-DD HH:mm:ss");

        arr.push({
          Room: report[i]?.["room.Room"],
          Entry: report[i]?.Entry,
          Exit: report[i]?.Exit,
          "UTC Time (+00:00)": utcFormatted,
          "Local Time": localTimeFormatted,
        });
      }
    }

    return arr;
  };

  const minKPI = (data, param, KPI = "", roomData = []) => {
    let min = Infinity;
    let count = 0;
    let res = "N/A";

    if (!data) {
      return res;
    }

    if (data.length === 0) return res;

    for (let i = 0; i < data.length; i++) {
      if (data[i][param] >= 0) {
        let value = data[i][param];

        if (KPI === "Space Utilization") {
          value =
            (value * 100) / peoplePer100sqft > 100
              ? 100
              : (value * 100) / peoplePer100sqft; // Refer the logic
        }

        if (KPI === "Occupancy Utilization") {
          const roomID = data[i]["RoomID"];
          const noOfChair = roomData?.find((item) => item.RoomID === roomID)
            ?.NoOfChair
            ? roomData?.find((item) => item.RoomID === roomID)?.NoOfChair
            : 1;

          value = data[i][param] / noOfChair;
        }

        if (value < min) {
          min = value;
        }
        count++;
      }
    }

    if (count > 0) {
      res = min.toFixed(0);
    }

    return res;
  };

  const maxKPI = (data, param, KPI = "", roomData = []) => {
    let max = -Infinity;
    let count = 0;
    let res = "N/A";

    if (!data) {
      return res;
    }

    if (data.length === 0) return res;

    for (let i = 0; i < data.length; i++) {
      if (data[i][param] >= 0) {
        let value = data[i][param];

        if (KPI === "Space Utilization") {
          value =
            (value * 100) / peoplePer100sqft > 100
              ? 100
              : (value * 100) / peoplePer100sqft; // Refer the logic
        }

        if (KPI === "Occupancy Utilization") {
          const roomID = data[i]["RoomID"];
          const noOfChair = roomData?.find((item) => item.RoomID === roomID)
            ?.NoOfChair
            ? roomData?.find((item) => item.RoomID === roomID)?.NoOfChair
            : 1;

          value = data[i][param] / noOfChair;
        }

        if (value > max) {
          max = value;
        }
        count++;
      }
    }

    if (count > 0) {
      res = max.toFixed(0);
    }

    return res;
  };

  const avgNoOfPeople = (data, param, exact) => {
    let total = 0;
    let count = 0;
    let res = 0;

    if (!data) {
      return "N/A";
    }

    if (data.length === 0) return "N/A";

    for (let i = 0; i < data.length; i++) {
      if (data[i][param] >= 0) {
        total += data[i][param];
        count++;
      }
    }

    if (total && count) {
      res = Math.round(total / count);
    }

    return res;
  };

  const avgNoOfEmptySeat = (data, param, exact) => {
    let total = 0;
    let count = 0;
    let res = 0;

    if (!data) {
      return "N/A";
    }

    if (data.length === 0) return "N/A";

    for (let i = 0; i < data.length; i++) {
      if (data[i][param] >= 0) {
        total += data[i][param];
        count++;
      }
    }

    if (total && count) {
      res = Math.round(total / count);
    }

    return res;
  };

  const avgUtilization = (data, param, type, roomData) => {
    let total = 0;
    let count = 0;
    let utilizationRate = 0;

    if (!data || data.length === 0) {
      return "N/A";
    }

    for (let i = 0; i < data.length; i++) {
      const value = data[i][param];
      if (value >= 0) {
        let calculatedUltilization;
        if (type === "Space Ultilization") {
          // Logic: Space Utilization
          calculatedUltilization = (value * 100) / peoplePer100sqft;
        } else {
          // Logic: Occupancy Utilization
          const roomID = data[i]["RoomID"];

          const noOfChair = roomData?.find((item) => item.RoomID === roomID)
            ?.NoOfChair
            ? roomData?.find((item) => item.RoomID === roomID)?.NoOfChair
            : 1;

          calculatedUltilization = (value / noOfChair) * 100;
        }

        // Cap the utilization at 100%
        data[i]["calculatedUltilization"] = Math.min(
          calculatedUltilization,
          100
        );

        // Accumulate totals for average calculation
        total += data[i]["calculatedUltilization"];
        count++;
      }
    }

    // Calculate average utilization rate
    utilizationRate = count > 0 ? total / count : "N/A";

    return utilizationRate;
  };

  const top5RoomUtilization = (data, roomData = []) => {
    let result = _(data)
      .groupBy("RoomID")
      .map((entries, room) => {
        const roomID = room;
        const roomName = entries[0].room.Room;

        const noOfChair = roomData?.find((item) => item.RoomID == roomID)
          ?.NoOfChair
          ? roomData?.find((item) => item.RoomID == roomID)?.NoOfChair
          : 1;

        const noOfPeople = _.meanBy(entries, (item) => item.NumberOfPersons);

        const noOfEmptySeat = _.meanBy(
          entries,
          (item) => item.NumberOfEmptySeats
        );

        const utilizationRate = _.meanBy(entries, (item) =>
          (item.NumberOfPersons / noOfChair) * 100 > 100
            ? 100
            : (item.NumberOfPersons / noOfChair) * 100
        );

        const spaceUtilizationRate = _.meanBy(entries, (item) =>
          (item.NumberOfPersons * 100) / peoplePer100sqft > 100
            ? 100
            : (item.NumberOfPersons * 100) / peoplePer100sqft
        );

        return {
          Room: roomName ? roomName.split(" - ")[0] : "",
          noOfPeople,
          noOfEmptySeat,
          noOfChair,
          utilizationRate,
          spaceUtilizationRate,
          trend: entries, // Include original entries for trend if needed
        };
      })
      .orderBy("utilizationRate", "desc")
      .take(5)
      .value();

    console.log("test my final result", result);
    return result;
  };

  const groupNoOfPeople = (arr) => {
    const obj = {};
    if (arr && arr.length) {
      for (let i = 0; i < arr.length; i++) {
        const roomID = arr[i].RoomID;
        const numberOfPersons = arr[i].NumberOfPersons;
        const NoP_front = arr[i].NoP_front;
        const NoP_back = arr[i].NoP_back;
        const NoP_left = arr[i].NoP_left;
        const NoP_right = arr[i].NoP_right;

        if (obj[roomID] === undefined) {
          obj[roomID] = {
            avg: numberOfPersons, // Sum of persons
            NoP_front: NoP_front ? NoP_front : 0,
            NoP_back: NoP_back ? NoP_back : 0,
            NoP_left: NoP_left ? NoP_left : 0,
            NoP_right: NoP_right ? NoP_right : 0,
            count: 1,
            max: numberOfPersons, // Initial max value
          };
        } else {
          obj[roomID].avg += numberOfPersons; // Increment sum
          obj[roomID].NoP_front += NoP_front;
          obj[roomID].NoP_back += NoP_back;
          obj[roomID].NoP_left += NoP_left;
          obj[roomID].NoP_right += NoP_right;
          obj[roomID].count++; // Increment count
          obj[roomID].max = Math.max(obj[roomID].max, numberOfPersons); // Update max
        }
      }
    }

    return obj;
  };

  const groupNoOfEmptySeat = (arr) => {
    const obj = {};
    if (arr && arr.length) {
      for (let i = 0; i < arr?.length; i++) {
        const roomID = arr[i].RoomID;
        const numberOfEmptySeats = arr[i].NumberOfEmptySeats;
        const NoES_front = arr[i].NoES_front;
        const NoES_back = arr[i].NoES_back;
        const NoES_left = arr[i].NoES_left;
        const NoES_right = arr[i].NoES_right;

        if (obj[roomID] === undefined) {
          obj[roomID] = {
            avg: numberOfEmptySeats,
            NoES_front: NoES_front ? NoES_front : 0,
            NoES_back: NoES_back ? NoES_back : 0,
            NoES_left: NoES_left ? NoES_left : 0,
            NoES_right: NoES_right ? NoES_right : 0,
            max: numberOfEmptySeats, // Initial max value
            count: 1,
          };
        } else {
          obj[roomID].avg += numberOfEmptySeats;
          obj[roomID].NoES_front += NoES_front;
          obj[roomID].NoES_back += NoES_back;
          obj[roomID].NoES_left += NoES_left;
          obj[roomID].NoES_right += NoES_right;
          obj[roomID].max = Math.max(obj[roomID].max, numberOfEmptySeats);
          obj[roomID].count++;
        }
      }
    }

    return obj;
  };

  const groupNoOfEmptySeatChart = (arr, param) => {
    const obj = {};

    // Get max seat per room
    if (arr && arr.length) {
      for (let i = 0; i < arr?.length; i++) {
        if (obj[arr[i].RoomID] === undefined) {
          obj[arr[i].RoomID] = {
            max: arr[i].NumberOfEmptySeats,
          };
        } else {
          obj[arr[i].RoomID].max = Math.max(
            obj[arr[i].RoomID].max,
            arr[i].NumberOfEmptySeats
          );
        }
      }
    }

    for (let i = 0; i < arr.length; i++) {
      const roomId = arr[i].RoomID;
      arr[i].max = obj[roomId].max;
    }

    return arr;
  };

  // Function: Group space utilization by room for heat map
  const groupSpaceUtilization = (arr) => {
    const obj = {};
    if (arr && arr.length) {
      for (let i = 0; i < arr?.length; i++) {
        if (obj[arr[i].RoomID] === undefined) {
          obj[arr[i].RoomID] = {
            avg:
              (arr[i].NumberOfPersons * 100) / peoplePer100sqft > 100
                ? 100
                : (arr[i].NumberOfPersons * 100) / peoplePer100sqft,
            count: 1,
          };
        } else {
          obj[arr[i].RoomID].avg +=
            (arr[i].NumberOfPersons * 100) / peoplePer100sqft > 100
              ? 100
              : (arr[i].NumberOfPersons * 100) / peoplePer100sqft;
          obj[arr[i].RoomID].count++;
        }
      }
    }
    return obj;
  };

  const groupSpaceUtilizationChart = (arr) => {
    let newArr;

    if (arr && arr.length) {
      newArr = arr?.map((item) => {
        return {
          ...item,
          avgUtil:
            (item.NumberOfPersons * 100) / peoplePer100sqft > 100
              ? 100
              : (item.NumberOfPersons * 100) / peoplePer100sqft,
        };
      });
    }

    return newArr;
  };

  const groupOccupancyUtilizationChart = (arr, roomData = []) => {
    let newArr;

    console.log("Test space UT: ", arr);
    console.log("Test room Data: ", roomData);

    if (arr && arr.length && roomData.length) {
      newArr = arr?.map((item) => {
        const roomID = item.RoomID;
        let noOfChair = roomData?.find((item) => item.RoomID === roomID)
          ?.NoOfChair
          ? roomData?.find((item) => item.RoomID === roomID)?.NoOfChair
          : 1;

        return {
          ...item,
          avgUtil:
            (item.NumberOfPersons / noOfChair) * 100 > 100
              ? 100
              : (item.NumberOfPersons / noOfChair) * 100,
        };
      });
    }

    return newArr;
  };

  // Function: Group occupancy utilization by room for heat map
  const groupOccupancyUtilization = (arr, roomData = []) => {
    let newArr;
    const obj = {};

    if (arr && arr.length && roomData.length) {
      newArr = arr?.map((item) => {
        const roomID = item.RoomID;
        const noOfChair = roomData?.find((item) => item.RoomID === roomID)
          ?.NoOfChair
          ? roomData?.find((item) => item.RoomID === roomID)?.NoOfChair
          : 1;

        return {
          ...item,
          avgUtil:
            (item.NumberOfPersons / noOfChair) * 100 > 100
              ? 100
              : (item.NumberOfPersons / noOfChair) * 100,
        };
      });
    }

    if (newArr && newArr.length) {
      for (let i = 0; i < newArr?.length; i++) {
        if (obj[newArr[i].RoomID] === undefined) {
          obj[newArr[i].RoomID] = {
            avg: newArr[i].avgUtil > 100 ? 100 : newArr[i].avgUtil,
            count: 1,
          };
        } else {
          obj[newArr[i].RoomID].avg +=
            newArr[i].avgUtil > 100 ? 100 : newArr[i].avgUtil;
          obj[newArr[i].RoomID].count++;
        }
      }
    }

    console.log("test my obje", newArr);
    return obj;
  };

  const groupPeakOccupancyUtilization = (arr, roomData = []) => {
    let newArr;
    const obj = {};

    if (arr && arr.length && roomData.length) {
      newArr = arr.filter(
        (item) => item.HourOfDay >= 9 && item.HourOfDay <= 17
      );

      newArr = newArr?.map((item) => {
        const roomID = item.RoomID;
        const noOfChair = roomData?.find((item) => item.RoomID === roomID)
          ?.NoOfChair
          ? roomData?.find((item) => item.RoomID === roomID)?.NoOfChair
          : 1;

        return {
          ...item,
          avgUtil:
            (item.NumberOfPersons / noOfChair) * 100 > 100
              ? 100
              : (item.NumberOfPersons / noOfChair) * 100,
        };
      });
    }

    if (newArr && newArr.length) {
      for (let i = 0; i < newArr?.length; i++) {
        let maxUtil = newArr[i].avgUtil;
        if (obj[newArr[i].RoomID] === undefined) {
          obj[newArr[i].RoomID] = {
            max: newArr[i].avgUtil,
            count: 1,
          };
        } else {
          obj[newArr[i].RoomID].max > maxUtil
            ? (obj[newArr[i].RoomID].max = obj[newArr[i].RoomID].max)
            : (obj[newArr[i].RoomID].max = maxUtil);

          obj[newArr[i].RoomID].count++;
        }
      }
    }

    console.log("test peak occu obj", obj);

    return obj;
  };

  // Function: to get the total and round up if needed
  const totalEntryExitMetric = (data, param, exact) => {
    let total = 0;
    let count = 0;
    let res = 0;

    // Check if data is available
    if (!data) {
      return "N/A";
    }
    if (data[param].length === 0) return "N/A";
    for (let i = 0; i < data[param].length; i++) {
      if (data[param][i]) {
        total += data[param][i][param];
        count++;
      }
    }

    if (total && count) {
      res = exact
        ? (Math.round((total / count) * 100) / 100).toFixed(2)
        : Math.round(total / count);
    }

    return res;
  };

  // Function: to get the total and round up if needed by location
  const totalEntryExitMetricByLocation = (data, param, exact) => {
    let rooms;
    let resData = [];

    // Get entrance's room ID
    if (floor) {
      rooms = floor.rooms.filter((room) => {
        if (
          room.Room.includes("Entrance") ||
          room.Room.includes("Exit") ||
          room.Room.includes("Counter")
        ) {
          return room;
        }
      });
    }

    // Check if data is available
    if (!data) {
      return "N/A";
    }

    if (data[param].length === 0) return "N/A";

    // Map Room and Total Entry / Total Exit
    rooms.forEach((room) => {
      let total = 0;
      let count = 0;
      let res = 0;
      let temp = {};

      for (let i = 0; i < data[param].length; i++) {
        if (data[param][i] && room.id === data[param][i]["roomId"]) {
          total += data[param][i][param];
          count++;
        }
      }
      if (total && count) {
        res = exact
          ? (Math.round((total / count) * 100) / 100).toFixed(2)
          : Math.round(total / count);
      }

      if (res) {
        temp["roomId"] = room.id;
        temp[param] = res;
        resData.push(temp);
      }
    });

    return resData;
  };

  // Function: to group entry, exit and Occupancy by day of the week
  const EntryExitByWeekDay = (data) => {
    // Check if data is available
    if (!data) {
      return "N/A";
    }

    const dayOrder = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };

    const mergedData = [...data.Entry, ...data.Exit, ...data.Occupancy];

    const groupedByDay = mergedData.reduce((acc, data) => {
      const day = data.dayOfWeek;
      if (!acc[day]) {
        acc[day] = {
          DayOfWeek: day,
          totalEntry: 0,
          totalExit: 0,
          totalOccupancy: 0,
          count: 0,
        };
      }
      acc[day].totalEntry += data.Entry || 0;
      acc[day].totalExit += data.Exit || 0;
      acc[day].totalOccupancy += data.Occupancy || 0;
      acc[day].count += 1;

      return acc;
    }, {});

    // Format data following the positive negative bar chart
    const res = Object.values(groupedByDay).map((day) => ({
      DayOfWeek: day.DayOfWeek,
      AverageEntry: Math.round(day.totalEntry / day.count) || 0,
      AverageExit: Math.round(day.totalExit / day.count) || 0,
      AverageOccupancy: Math.round(day.totalOccupancy / day.count) || 0,
    }));

    // Sort the data by day of the week
    const sortedRes = res.sort(
      (a, b) => dayOrder[a.DayOfWeek] - dayOrder[b.DayOfWeek]
    );

    return sortedRes;
  };

  const EntryExitByHour = (data) => {
    if (!data) {
      return "N/A";
    }

    const hourOrder = {
      "09 AM": 0,
      "10 AM": 1,
      "11 AM": 2,
      "12 PM": 3,
      "01 PM": 4,
      "02 PM": 5,
      "03 PM": 6,
      "04 PM": 7,
      "05 PM": 8,
      "06 PM": 9,
    };

    const mergedData = [...data.Entry, ...data.Exit, ...data.Occupancy];

    const groupedByHour = mergedData.reduce((acc, data) => {
      const hour24 = parseInt(moment(data.DateTime).local().format("HH"), 10);
      const hour12 = moment(data.DateTime).local().format("hh A");

      if (hour24 < 9 || hour24 > 18) {
        return acc; // Skip data outside of 9 AM - 6 PM
      }

      if (!acc[hour12]) {
        acc[hour12] = {
          HourOfDay: hour12,
          totalEntry: 0,
          totalExit: 0,
          totalOccupancy: 0,
          count: 0,
        };
      }
      acc[hour12].count += 1;
      acc[hour12].totalEntry += data.Entry || 0;
      acc[hour12].totalExit += data.Exit || 0;
      acc[hour12].totalOccupancy += data.Occupancy || 0;

      return acc;
    }, {});

    // Format data following the positive negative bar chart
    const res = Object.values(groupedByHour).map((hour) => ({
      HourOfDay: hour.HourOfDay,
      AverageEntry: Math.round(hour.totalEntry / (hour.count / 3)) || 0,
      AverageExit: Math.round(hour.totalExit / (hour.count / 3)) * -1 || 0,
      AverageOccupancy:
        Math.round(hour.totalEntry / (hour.count / 3)) +
          Math.round(hour.totalExit / (hour.count / 3)) * -1 || 0,
    }));

    // Sort the data by day of the week
    const sortedRes = res.sort(
      (a, b) => hourOrder[a.HourOfDay] - hourOrder[b.HourOfDay]
    );

    return sortedRes;
  };

  const EntryExitByDateHour = (data, range) => {
    let uniquePeriod_Xaxis;
    let groupedData;
    let aggregateData;
    let betweenDates = 5;

    // Check if data is available
    if (!data && data?.length) {
      return "N/A";
    }

    if (range && range?.length) {
      if (range[0].isValid && range[1].isValid) {
        const startDT = range[0].clone().local().startOf("day");
        const endDT = range[1].clone().local().startOf("day");
        betweenDates = endDT?.diff(startDT, "days");
      }
    }

    console.log("betweenDates", betweenDates);

    // Selected range more than 16 days, show the last 16 days
    if (betweenDates > 16) {
      const newDataRange = [
        range[1].clone().local().startOf("day").subtract(7, "days"),
        range[1].clone().local().startOf("day"),
      ];

      const last16DaysData = _.filter(data, (item) => {
        const dataDT = moment(item.enqueuedTime_Stamp);
        return dataDT.isBetween(newDataRange[0], newDataRange[1], []);
      });
      // Step 1: Group by month and hour
      groupedData = _.groupBy(last16DaysData, (item) => {
        const month = item.Month;
        const day = item.Day;
        const hour = item.HourOfDay;
        return `${month}-${day}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, day, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month + "/" + day,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgEntry: Math.floor(_.meanBy(entries, "Entry")),
          AvgExit: Math.floor(_.meanBy(entries, "Exit")),
          AvgOccupancy:   Math.abs(
              Math.floor(_.meanBy(entries, "Entry")) -
                Math.floor(_.meanBy(entries, "Exit"))
            ) <= 0
              ? 0
              : Math.abs(
                  Math.floor(_.meanBy(entries, "Entry")) -
                    Math.floor(_.meanBy(entries, "Exit"))
                ),
        };
      });
    }
    // show all data points
    else {
      // Step 1: Group by month and hour
      groupedData = _.groupBy(data, (item) => {
        const month = item.Month;
        const day = item.Day;
        const hour = item.HourOfDay;
        return `${month}-${day}-${hour}`;
      });


      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, day, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month + "/" + day,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgEntry: Math.floor(_.meanBy(entries, "Entry")),
          AvgExit: Math.floor(_.meanBy(entries, "Exit")),
          AvgOccupancy:  Math.abs(
              Math.floor(_.meanBy(entries, "Entry")) -
                Math.floor(_.meanBy(entries, "Exit"))
            ) <= 0
              ? 0
              : Math.abs(
                  Math.floor(_.meanBy(entries, "Entry")) -
                    Math.floor(_.meanBy(entries, "Exit"))
                ),
        };
      });

    }
    uniquePeriod_Xaxis = [
      ...new Set(aggregateData?.map((item) => item.AggregatedPeriod_Xaxis)),
    ];


    return {
      aggregateData: aggregateData,
      uniquePeriod_Xaxis: uniquePeriod_Xaxis,
    };
  };

  // Net Entry Exit By Date and Hour
  const NoOfPeopleByDateHour = (data, range) => {
    let uniquePeriod_Xaxis;
    let groupedData;
    let aggregateData;
    let betweenDates = 5;

    // Check if data is available
    if (!data && data?.length) {
      return "N/A";
    }

    if (range && range?.length) {
      if (range[0].isValid && range[1].isValid) {
        const startDT = range[0].clone().local().startOf("day");
        const endDT = range[1].clone().local().startOf("day");
        betweenDates = endDT?.diff(startDT, "days");
      }
    }

    // Selected range more than 16 days, show months
    if (betweenDates > 16) {
      // Step 1: Group by month and hour
      groupedData = _.groupBy(data, (item) => {
        const month = item.Month;
        const hour = item.HourOfDay;
        return `${month}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgNoOfPeople: _.meanBy(entries, "NumberOfPersons").toFixed(0),
        };
      });
    }
    // show all data points
    else {
      // Step 1: Group by month and hour
      groupedData = _.groupBy(data, (item) => {
        const month = item.Month;
        const day = item.Day;
        const hour = item.HourOfDay;
        return `${month}-${day}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, day, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month + "/" + day,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgNoOfPeople: _.meanBy(entries, "NumberOfPersons").toFixed(0),
        };
      });
    }
    uniquePeriod_Xaxis = [
      ...new Set(aggregateData?.map((item) => item.AggregatedPeriod_Xaxis)),
    ];

    return {
      aggregateData: aggregateData,
      uniquePeriod_Xaxis: uniquePeriod_Xaxis,
    };
  };

  // Net Entry Exit by Date and Hour
  const NetEntryExitByDateHour = (data, range) => {
    let uniquePeriod_Xaxis;
    let groupedData;
    let aggregateData;
    let betweenDates = 5;

    // Check if data is available
    if (!data && data?.length) {
      return "N/A";
    }

    if (range && range?.length) {
      if (range[0].isValid && range[1].isValid) {
        const startDT = range[0].clone().local().startOf("day");
        const endDT = range[1].clone().local().startOf("day");
        betweenDates = endDT?.diff(startDT, "days");
      }
    }

    // Selected range more than 16 days, show months
    if (betweenDates > 16) {
      const newDataRange = [
        range[1].clone().local().startOf("day").subtract(7, "days"),
        range[1].clone().local().startOf("day"),
      ];

      const last16DaysData = _.filter(data, (item) => {
        const dataDT = moment(item.enqueuedTime_Stamp);
        return dataDT.isBetween(newDataRange[0], newDataRange[1], []);
      });

      // Step 1: Group by month and hour
      groupedData = _.groupBy(last16DaysData, (item) => {
        const month = item.Month;
        const day = item.Day;
        const hour = item.HourOfDay;
        return `${month}-${day}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, day, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month + "/" + day,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgEntry: Math.floor(_.meanBy(entries, "Entry")),
          AvgExit: Math.floor(_.meanBy(entries, "Exit")),
          AvgOccupancy:
            Math.abs(
              Math.floor(_.meanBy(entries, "Entry")) -
                Math.floor(_.meanBy(entries, "Exit"))
            ) <= 0
              ? 0
              : Math.abs(
                  Math.floor(_.meanBy(entries, "Entry")) -
                    Math.floor(_.meanBy(entries, "Exit"))
                ),
        };
      });
    }
    // show all data points
    else {
      // Step 1: Group by month and hour
      groupedData = _.groupBy(data, (item) => {
        const month = item.Month;
        const day = item.Day;
        const hour = item.HourOfDay;
        return `${month}-${day}-${hour}`;
      });

      // Step 2: Aggregate data
      aggregateData = _.map(groupedData, (entries, key) => {
        const [month, day, hour] = key.split("-");

        return {
          AggregatedPeriod_Xaxis: month + "/" + day,
          AggregatedHour_Yaxis: parseInt(hour),
          AvgEntry: Math.floor(_.meanBy(entries, "Entry")),
          AvgExit: Math.floor(_.meanBy(entries, "Exit")),
          AvgOccupancy:
            Math.abs(
              Math.floor(_.meanBy(entries, "Entry")) -
                Math.floor(_.meanBy(entries, "Exit"))
            ) <= 0
              ? 0
              : Math.abs(
                  Math.floor(_.meanBy(entries, "Entry")) -
                    Math.floor(_.meanBy(entries, "Exit"))
                ),
        };
      });
    }
    uniquePeriod_Xaxis = [
      ...new Set(aggregateData?.map((item) => item.AggregatedPeriod_Xaxis)),
    ];

    return {
      aggregateData: aggregateData,
      uniquePeriod_Xaxis: uniquePeriod_Xaxis,
    };
  };

  const PeakOccupancyByHourLocation = (data, roomData = []) => {
    let uniquePeriod_Xaxis;
    let groupedData;
    let aggregateData;
    let avgPeakOccupancy;
    let maxPeakOccupancy;
    let minPeakOccupancy;

    // Check if data is available
    if (!data && data?.length) {
      return "N/A";
    }

    console.log("tessss aggr", data);

    // Filtered data from 9am-5pm
    let filteredData = data.filter(
      (item) => item.HourOfDay >= 9 && item.HourOfDay <= 17
    );

    // Step 1: Group by month and hour
    groupedData = _.groupBy(filteredData, (item) => {
      const roomId = item.Room;
      const hour = item.HourOfDay;
      return `${hour}-${roomId}`;
    });

    // Step 2: Aggregate data
    aggregateData = _.map(groupedData, (entries, key) => {
      const [hour, roomId] = key.split("-");
      const numberOfChair = roomData?.find(
        (item) => item.RoomID === entries[0]["RoomID"]
      )["NoOfChair"]
        ? roomData?.find((item) => item.RoomID === entries[0]["RoomID"])[
            "NoOfChair"
          ]
        : 1;

      return {
        AggregatedPeriod_Xaxis: roomId,
        AggregatedHour_Yaxis: parseInt(hour),
        AvgOccupancy:
          (
            (_.maxBy(entries, "NumberOfPersons").NumberOfPersons /
              numberOfChair) *
            100
          ).toFixed(0) > 100
            ? 100
            : (
                (_.maxBy(entries, "NumberOfPersons").NumberOfPersons /
                  numberOfChair) *
                100
              ).toFixed(0),
      };
    });

    // Average Peak Occupancy
    avgPeakOccupancy = Math.round(
      _.mean(aggregateData?.map((item) => parseFloat(item.AvgOccupancy)))
    );

    // Max Peak Occupancy
    maxPeakOccupancy = Math.round(
      _.maxBy(aggregateData?.map((item) => parseFloat(item.AvgOccupancy)))
    );

    // Min Peak Occupancy
    minPeakOccupancy = Math.round(
      _.minBy(aggregateData?.map((item) => parseFloat(item.AvgOccupancy)))
    );

    // Peak Occupancy by Location  and Hour
    uniquePeriod_Xaxis = [
      ...new Set(aggregateData?.map((item) => item.AggregatedPeriod_Xaxis)),
    ];

    return {
      avgPeakOccupancy: avgPeakOccupancy,
      maxPeakOccupancy: maxPeakOccupancy,
      minPeakOccupancy: minPeakOccupancy,
      aggregateData: aggregateData,
      uniquePeriod_Xaxis: uniquePeriod_Xaxis,
    };
  };

  // Aggregate data for the metrics and charts
  const aggregateData = useMemo(() => {
    return {
      // Entry Exit Metrics
      TotalEntry: filteredPerformance
        ? totalEntryExitMetric(filteredPerformance.rawData, "Entry")
        : true,
      TotalExit: filteredPerformance
        ? totalEntryExitMetric(filteredPerformance.rawData, "Exit")
        : true,
      EntryExitByWeekDay: filteredPerformance
        ? EntryExitByWeekDay(filteredPerformance.rawData)
        : "",
      EntryExitByHour: filteredPerformance
        ? EntryExitByHour(filteredPerformance.rawData)
        : "",
      EntryExitByDateHour: filteredPerformance
        ? EntryExitByDateHour(filteredPerformance.rawData?.EntryExitRaw, [
            date[0],
            date[1],
          ])
        : "",
      TotalEntryByLocation: filteredPerformance
        ? totalEntryExitMetricByLocation(filteredPerformance.rawData, "Entry")
        : "",
      TotalExitByLocation: filteredPerformance
        ? totalEntryExitMetricByLocation(filteredPerformance.rawData, "Exit")
        : "",
      TotalPeople: filteredPerformance
        ? totalEntryExitMetric(filteredPerformance.rawData, "Occupancy")
        : true,

      // Number of People
      NoOfPeople: filteredPerformance
        ? avgNoOfPeople(filteredPerformance?.NumberOfPersons, "NumberOfPersons")
        : "",
      MinNoOfPeople: filteredPerformance
        ? minKPI(filteredPerformance.NumberOfPersons, "NumberOfPersons")
        : "",
      MaxNoOfPeople: filteredPerformance
        ? maxKPI(filteredPerformance.NumberOfPersons, "NumberOfPersons")
        : "",
      NoOfPeopleByLocation: filteredPerformance
        ? groupNoOfPeople(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons"
          )
        : "",
      NoOfPeopleByDateHour: filteredPerformance
        ? NoOfPeopleByDateHour(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons"
          )
        : true,

      // Number of Empty Seat
      NoOfEmptySeat: filteredPerformance
        ? avgNoOfEmptySeat(
            filteredPerformance?.NumberOfEmptySeats,
            "NumberOfEmptySeats"
          )
        : "",
      MinNoOfEmptySeat: filteredPerformance
        ? minKPI(filteredPerformance.NumberOfEmptySeats, "NumberOfEmptySeats")
        : "",
      MaxNoOfEmptySeat: filteredPerformance
        ? maxKPI(filteredPerformance.NumberOfEmptySeats, "NumberOfEmptySeats")
        : "",
      NoOfEmptySeatByLocation: filteredPerformance
        ? groupNoOfEmptySeat(
            filteredPerformance.NumberOfEmptySeats,
            "NumberOfEmptySeats"
          )
        : "",
      NoOfEmptySeatChart: filteredPerformance
        ? groupNoOfEmptySeatChart(
            filteredPerformance.NumberOfEmptySeats,
            "NumberOfEmptySeats"
          )
        : "",
      NetEntryExitByDateHour: filteredPerformance
        ? NetEntryExitByDateHour(filteredPerformance.rawData?.EntryExitRaw, [
            date[0],
            date[1],
          ])
        : "",

      // Occupancy
      PeakOccupancyByHourLocation: filteredPerformance
        ? PeakOccupancyByHourLocation(
            filteredPerformance?.NumberOfPersons,
            filteredPerformance?.ChairPerRoom
          )
        : "",
      PeakOccupancyByLocation: filteredPerformance
        ? groupPeakOccupancyUtilization(
            filteredPerformance?.NumberOfPersons,
            filteredPerformance?.ChairPerRoom
          )
        : "",
      AvgOccupancyUtilization: filteredPerformance
        ? avgUtilization(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons",
            "Occupancy Utilization",
            filteredPerformance?.ChairPerRoom
          )
        : "",
      Top5RoomUtilization: filteredPerformance
        ? top5RoomUtilization(
            filteredPerformance.Report,
            filteredPerformance.ChairPerRoom
          )
        : "",
      MinOccupancyUtilization: filteredPerformance
        ? minKPI(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons",
            "Space Utilization",
            filteredPerformance?.ChairPerRoom
          )
        : "",
      MaxOccupancyUtilization: filteredPerformance
        ? maxKPI(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons",
            "Space Utilization",
            filteredPerformance?.ChairPerRoom
          )
        : "",
      OccupancyUtilizationByLocation: filteredPerformance
        ? groupOccupancyUtilization(
            filteredPerformance?.NumberOfPersons,
            filteredPerformance.ChairPerRoom
          )
        : "",
      OccupancyUtilizationChart: filteredPerformance
        ? groupOccupancyUtilizationChart(
            filteredPerformance.NumberOfPersons,
            filteredPerformance?.ChairPerRoom
          )
        : "",

      // Space Utilization
      AvgSpaceUtilization: filteredPerformance
        ? avgUtilization(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons",
            "Space Ultilization",
            filteredPerformance?.ChairPerRoom
          )
        : "",
      SpaceUtilizationByLocation: filteredPerformance
        ? groupSpaceUtilization(filteredPerformance?.NumberOfPersons)
        : "",
      MinSpaceUtilization: filteredPerformance
        ? minKPI(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons",
            "Space Utilization"
          )
        : "",
      MaxSpaceUtilization: filteredPerformance
        ? maxKPI(
            filteredPerformance.NumberOfPersons,
            "NumberOfPersons",
            "Space Utilization"
          )
        : "",
      SpaceUtilizationChart: filteredPerformance
        ? groupSpaceUtilizationChart(filteredPerformance.NumberOfPersons)
        : "",
    };
  }, [filteredPerformance]);

  // Effect on initial load:if user is not authenticated or building or floor not selected, navigate to dashboard page
  useEffect(() => {
    if (!dashboard || !userInfo || !floor || !building) {
      return navigate("/dashboard");
    }
  }, [floor, building, userInfo]);

  // Effect on initial load: When first loading the page, run the fetch Performance data
  useEffect(() => {
    if (performance === undefined && userInfo) {
      dispatch(
        loadPerformance(
          `between=${date[0]}--${
            date[1]
          }&params=NumberOfPersons,NumberOfEmptySeats,enqueuedTime_Stamp&user=${
            userInfo?.username
          }${building ? `&building=${building.id}` : ""}${
            floor ? `&floor=${floor.id}` : ""
          }`
        )
      );
    }
  }, [loadPerformance, userInfo]);

  // Effect on load performance and filtered performance data
  useEffect(() => {
    if (performance && !filteredPerformance) {
      setFilteredPerformance(performance);
    }
    if (!performance && filteredPerformance) {
      setFilteredPerformance(performance);
    }
  }, [performance, filteredPerformance]);

  // Effect to reset the data based on selection of the date time slider
  useEffect(() => {
    if (performance) {
      setDate([
        moment(performance.dateTimeRange.min),
        moment(performance.dateTimeRange.max),
      ]);
      setSelectedTime([
        moment(performance.dateTimeRange.min),
        moment(performance.dateTimeRange.max),
      ]);
      setMinDate(moment(performance.dateTimeRange.validMin));
      setMaxDate(moment(performance.dateTimeRange.validMax));
    }
  }, [performance, selectedAllRooms]);

  // Effect to initially set all rooms selected on heath heatmap
  useEffect(() => {
    setSelectedAllRooms("true");
  }, []);

  return (
    <div
      className="dashboard h-100 w-100  overflow-y-auto"
      style={{
        backgroundImage: `url('./images/building${dark ? "" : "-Light"}.png')`,
      }}
    >
      <div className="container-fluid px-0 pb-0 pt-4  h-100 w-100">
        {/* Upper part */}
        <div className="row w-100 h-66 flex-column flex-lg-row m-0 align-items-center">
          {/* Heatmap layout */}
          <div
            className="col col-lg-6 h-100 d-flex flex-column justify-content-center"
            style={{ position: "relative" }}
          >
            <CustomTooltip
              selectedKPI={selectedKPI}
              showDetail={showDetail}
              tooltipRoom={tooltipRoom}
              noOfPeople={aggregateData?.NoOfPeopleByLocation}
              noOfEmptySeat={aggregateData?.NoOfEmptySeatByLocation}
              rooms={floor?.rooms}
            />
            {floor && (
              <div
                className={`text-center ${
                  dark ? "text-white" : "text-main"
                } fs-4`}
              >
                {floor.floor}
              </div>
            )}

            {/* Total Number/TotalEnty/TotalExit Indicator of People Indicator */}
            {selectedKPI === 20 && (
              <div className="d-flex align-items-center w-100 justify-content-center gap-3 mt-1">
                <KPIIndicator
                  title={"Total No. People"}
                  value={aggregateData?.TotalPeople}
                  threshold={10}
                  // setModalChart={setModalChart}
                  // filteredHealth={filteredHealth}
                  // modalChart={modalChart}
                  dark={dark}
                />
                <KPIIndicator
                  title={"Total Entry"}
                  value={aggregateData?.TotalEntry}
                  // setModalChart={setModalChart}
                  // filteredHealth={filteredHealth}
                  // modalChart={modalChart}
                  dark={dark}
                />
                <KPIIndicator
                  title={"Total Exit"}
                  value={aggregateData?.TotalExit}
                  // setModalChart={setModalChart}
                  // filteredHealth={filteredHealth}
                  // modalChart={modalChart}
                  dark={dark}
                />
              </div>
            )}

            {/* Radio selection */}
            <div className="d-flex align-items-center justify-content-center gap-4 text-white mt-1">
              {/* Select All */}
              <div>
                <label className="d-flex justify-content-center align-items-center gap-1">
                  <input
                    type="radio"
                    value={true}
                    checked={selectedAllRooms === "true"}
                    onChange={(e) => {
                      setSelectedAllRooms(e.target.value);
                    }}
                  />
                  Select all
                </label>
              </div>

              {/* Deselect All */}
              <div>
                <label className="d-flex justify-content-center align-items-center gap-1">
                  <input
                    type="radio"
                    value={false}
                    checked={selectedAllRooms === "false"}
                    onChange={(e) => {
                      setSelectedAllRooms(e.target.value);
                    }}
                  />
                  Deselect all
                </label>
              </div>
            </div>

            {/* Zoom Controller Heatmap */}
            <div className="w-100 d-flex align-items-center justify-content-center mt-2">
              <ZoomController
                zoomIn={handleZoomIn}
                zoomOut={handleZoomOut}
                reset={handleReset}
              />
            </div>

            <div className="w-100 h-100 text-center mb-lg-0 mb-5 d-flex flex-column align-items-center justify-content-between overflow-hidden">
              {floor && (
                <>
                  <PanZoomController panZoomRef={panZoomRef}>
                    <PerformanceImage
                      userInfo={userInfo}
                      noOfPeople={aggregateData?.NoOfPeopleByLocation}
                      noOfEmptySeat={aggregateData?.NoOfEmptySeatByLocation}
                      spaceUtilization={
                        aggregateData?.SpaceUtilizationByLocation
                      }
                      occupancyUtilization={
                        aggregateData?.OccupancyUtilizationByLocation
                      }
                      peakOccupancyUtilization={
                        aggregateData?.PeakOccupancyByLocation
                      }
                      ranking={aggregateData?.Top5RoomUtilization}
                      totalEntryByLocation={aggregateData?.TotalEntryByLocation}
                      totalExitByLocation={aggregateData?.TotalExitByLocation}
                      selectedPattern={selectedPattern}
                      selectedKPI={selectedKPI}
                      building={building}
                      floor={floor}
                      rooms={rooms}
                      loadPerformance={loadPerformance}
                      setRooms={setRooms}
                      setRoom={setRoom}
                      setSelectedAllRooms={setSelectedAllRooms}
                      selectedAllRooms={selectedAllRooms}
                      setSelectedTime={setSelectedTime}
                      date={date}
                      setTooltipRoom={setTooltipRoom}
                      setTooltipPosition={setTooltipPosition}
                      showDetail={showDetail}
                      setShowDetail={setShowDetail}
                    />
                  </PanZoomController>
                </>
              )}
            </div>
            {/* <div
              className={`text-center ${
                dark ? "text-white" : "text-main"
              } fs-4`}
            >
              {room ? room.Room : "All rooms"}
            </div> */}
          </div>

          {/* Chart area */}
          <div className="col-6 h-100 d-flex flex-column  px-2 px-lg-3">
            <div className="w-100 align-items-center d-flex justify-content-end h-25 flex-column flex-lg-row ">
              <div className="h-25 d-flex align-items-center justify-content-center w-100 mb-5 mb-lg-0">
                <div className="w-100 d-flex flex-column align-items-center justify-content-center">
                  <div
                    className={`w-100 text-center ${dark ? "text-white" : ""}`}
                  >
                    <span>Selected Interval: </span>

                    <span>{moment(selectedTime[0]).format("HH:mm Do")}</span>
                    <span className="mx-2">-</span>
                    <span>{moment(selectedTime[1]).format("HH:mm Do")}</span>
                  </div>
                  <TimeRange
                    onUpdateCallback={(e) => setError(e.error)}
                    error={error}
                    ticksNumber={10}
                    className="text-white"
                    selectedInterval={selectedTime}
                    timelineInterval={date}
                    onChangeCallback={(selectedInterval) => {
                      if (filteredPerformance) {
                        const localToUTCFrom = moment(
                          selectedInterval[0]
                        ).format();
                        const localToUTCTo = moment(
                          selectedInterval[1]
                        ).format();

                        let filter = {};
                        console.log("test performance", performance);
                        for (let key in performance) {
                          // Processing Entry Exit data
                          if (key === "rawData") {
                            filter[key] = {};
                            for (let metric in performance[key]) {
                              filter[key][metric] = [];
                              for (
                                let k = 0;
                                k < performance[key][metric].length;
                                k++
                              ) {
                                let convertTimeStamp = moment(
                                  performance[key][metric][k].DateTime
                                )
                                  .local()
                                  .format();
                                if (
                                  convertTimeStamp >= localToUTCFrom &&
                                  convertTimeStamp <= localToUTCTo
                                ) {
                                  filter[key][metric].push(
                                    performance[key][metric][k]
                                  );
                                }
                              }
                            }
                          } else if (key === "ChairPerRoom") {
                            filter[key] = performance[key];
                          } else {
                            // Occupancy data
                            for (let i = 0; i < performance[key].length; i++) {
                              if (filter[key] === undefined) filter[key] = [];

                              let convertTimeStamp = moment(
                                performance[key][i].enqueuedTime_Stamp
                              )
                                .local()
                                .format();

                              if (
                                convertTimeStamp >= localToUTCFrom &&
                                convertTimeStamp <= localToUTCTo
                              ) {
                                filter[key].push(performance[key][i]);
                              }
                            }
                          }
                        }

                        setFilteredPerformance(filter);
                      }
                      setSelectedTime(selectedInterval);
                    }}
                    formatTick={(ms) => {
                      return moment(ms).format("HH:mm Do MMM");
                    }}
                  />
                </div>
              </div>

              {/* Date Picker */}
              <div className="">
                <DateRangePicker
                  clearIcon={null}
                  calendarIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      stroke={dark ? "white" : "black"}
                      stroke-width="2"
                      class="react-daterange-picker__calendar-button__icon react-daterange-picker__button__icon"
                    >
                      <rect
                        fill="none"
                        height="15"
                        width="15"
                        x="2"
                        y="2"
                      ></rect>
                      <line x1="6" x2="6" y1="0" y2="4"></line>
                      <line x1="13" x2="13" y1="0" y2="4"></line>
                    </svg>
                  }
                  onInvalidChange={() =>
                    alert(
                      "The date range picked is out of the project range. Please select the valid date range"
                    )
                  }
                  // minDate={minDate.toDate()}
                  // maxDate={maxDate.toDate()}
                  onChange={(e) => {
                    setDate(e);
                    setSelectedTime(e);
                    dispatch(
                      loadPerformance(
                        `between=${e[0]}--${
                          e[1]
                        }&params=NumberOfPersons,NumberOfEmptySeats&user=${
                          userInfo.username
                        }${building ? `&building=${building.id}` : ""}${
                          floor ? `&floor=${floor.id}` : ""
                        }${
                          room ? `&room=${room.id}` : ""
                        }&timeRangeSelected=true`
                      )
                    );

                    setFilteredPerformance(performance);
                  }}
                  value={date}
                />
              </div>

              {/* Export the data */}
              <div className="my-4 my-lg-0">
                {filteredPerformance &&
                (selectedKPI === 20) ? (
                  // Report 1: Entry Exit Report
                  <Export
                    data={format(filteredPerformance?.rawData?.EntryExitRaw, 1)}
                    name="Average Entry Exit Report"
                    dark={dark}
                  />
                ) :filteredPerformance && selectedKPI === 5?(
                   // Report 1: Entry Exit Activity Report
                  <Export
                    data={format(filteredPerformance?.rawData?.EntryExitRaw, 4)}
                    name="Entry Exit Activity Report"
                    dark={dark}
                  />
                ): filteredPerformance && selectedKPI === 7 ? (
                  // Report 3: Net Entry Exit Report
                  <Export
                    data={format(filteredPerformance?.rawData?.EntryExitRaw, 3)}
                    name="Net Entry Exit Report"
                    dark={dark}
                  />
                ) : (
                  // Report 2: Occupancy Report
                  <Export
                    data={format(filteredPerformance?.Report, 2)}
                    name="Occupancy Report"
                    dark={dark}
                  />
                )}
              </div>
            </div>

            {!selectedKPI && filteredPerformance && performance ? (
              <>
                {/* Default chart initial load */}
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "70%" }}>
                    <NoOfPeopleChart
                      data={filteredPerformance?.NumberOfPersons}
                      title="Occupancy by Location(s)"
                      datakey="NumberOfPersons"
                      conditions={{
                        Good: {
                          Min: 1,
                          Max: 2,
                        },
                        Moderate: {
                          Min: 3,
                          Max: 4,
                        },
                        Poor: {
                          Min: 0,
                          Max: 5,
                        },
                      }}
                    />
                  </div>
                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        This graph displays the number of people detected at
                        various locations within the office over time, offering
                        insight into foot traffic patterns, space utilization,
                        and activity hotspots across different areas.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 20 ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "70%" }}>
                    <ResponsiveContainer className="mt-4" minHeight={"100%"}>
                      {/* Logic 1- bar chart */}

                      <PositiveNegativeBarChart
                        data={aggregateData?.EntryExitByHour}
                      />
                    </ResponsiveContainer>
                  </div>

                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        The bar chart tracks hourly average office entries,
                        exits. The line graph calculates the net entry exit for
                        selected duration, providing insight into occupancy
                        trends, peak usage periods, and space utilization to
                        support capacity planning and operational efficiency.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 1 ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "70%" }}>
                    <NoOfPeopleChart
                      data={filteredPerformance?.NumberOfPersons}
                      title="Occupancy by Location(s)"
                      datakey="NumberOfPersons"
                      conditions={{
                        Good: {
                          Min: 1,
                          Max: 2,
                        },
                        Moderate: {
                          Min: 3,
                          Max: 4,
                        },
                        Poor: {
                          Min: 0,
                          Max: 5,
                        },
                      }}
                    />
                  </div>
                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        This graph displays the number of people detected at
                        various locations within the office over time, offering
                        insight into foot traffic patterns, space utilization,
                        and activity hotspots across different areas.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 2 ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "70%" }}>
                    <NoOfEmptySeatChart
                      data={aggregateData?.NoOfEmptySeatChart}
                      title="Number Of Seat(s) by Location(s)"
                      datakey="NumberOfEmptySeats"
                      conditions={{
                        Good: {
                          Min: 0,
                          Max: 0.3,
                        },
                        Moderate: {
                          Min: 0.3,
                          Max: 0.5,
                        },
                        Poor: {
                          Min: 0.5,
                          Max: -Infinity,
                        },
                      }}
                    />
                  </div>
                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        This graph illustrates the number of available seats
                        across different office locations over time, providing
                        visibility into seating capacity, usage trends, and
                        potential underutilized or overcrowded areas.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 3 ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "70%" }}>
                    <SpaceUtilizationChart
                      data={aggregateData?.SpaceUtilizationChart}
                      title="Comfort occupancy rate"
                      datakey="avgUtil"
                      conditions={{
                        Good: {
                          Min: 50,
                          Max: 100,
                        },
                        Moderate: {
                          Min: 30,
                          Max: 50,
                        },
                        Poor: {
                          Min: 0,
                          Max: 30,
                        },
                      }}
                    />
                  </div>
                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        Comfort occupancy rate reflects the average utilization
                        relative to IFMA's "comfortable" occupancy standard,
                        which is typically defined as 2 people per 100 square
                        feet. This metric helps evaluate how effectively an
                        inspected area is being used in terms of its comfort
                        level.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 4 ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "70%" }}>
                    <OccupancyUtilizationChart
                      data={aggregateData?.OccupancyUtilizationChart}
                      title="Space Utilization"
                      datakey="avgUtil"
                      conditions={{
                        Good: {
                          Min: 50,
                          Max: 100,
                        },
                        Moderate: {
                          Min: 19,
                          Max: 49,
                        },
                        Poor: {
                          Min: 0,
                          Max: 19,
                        },
                      }}
                    />
                  </div>
                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <div className="d-flex flex-column">
                        <p className="d-flex text-center text-white p-2">
                          This graph visualizes space utilization across
                          different office locations, calculated as the ratio of
                          people counted to available chairs over time,
                          highlighting occupancy levels, seating efficiency, and
                          potential over- or under-utilized areas.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 5 && aggregateData?.EntryExitByDateHour ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "80%" }}>
                    <HeatmapPlotlyChart
                      data={aggregateData?.EntryExitByDateHour}
                      type={1}
                    />
                  </div>

                  {/* Description about*/}
                  <div style={{ height: "20%" }}>
                    {/* Description about*/}
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        This graph tracks hourly office entries and exits by
                        date, providing insight into occupancy trends, peak
                        usage periods, and space utilization to support capacity
                        planning and operational efficiency.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 6 && aggregateData?.NoOfPeopleByDateHour ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "80%" }}>
                    <HeatmapPlotlyChart
                      data={aggregateData?.NoOfPeopleByDateHour}
                      type={3}
                    />
                  </div>
                  {/* Description about*/}
                  <div style={{ height: "20%" }}>
                    {/* Description about*/}

                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        Number of people by date hour is an aggregation of
                        number of people by hour
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 7 && aggregateData?.NetEntryExitByDateHour ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "80%" }}>
                    <HeatmapPlotlyChart
                      data={aggregateData?.NetEntryExitByDateHour}
                      type={4}
                    />
                  </div>
                  {/* Description about*/}
                  <div style={{ height: "20%" }}>
                    {/* Description about*/}

                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        This graph presents net occupancy by date and hour,
                        showing the number of people present after accounting
                        for entries and exits, providing insights into real-time
                        space utilization and occupancy patterns over time.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 8 &&
              aggregateData?.PeakOccupancyByHourLocation ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto "
                  style={{ height: "100%" }}
                >
                  <div style={{ height: "80%" }}>
                    <HeatmapPlotlyChart
                      data={aggregateData?.PeakOccupancyByHourLocation}
                      type={2}
                    />
                  </div>
                  {/* Description about*/}
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ height: "30%" }}
                  >
                    <div className="w-100 d-flex justify-content-around text-white">
                      <p className="d-flex text-center text-white p-2">
                        Peak Occupancy Utilization highlights the highest space
                        usage, helping identify patterns for optimizing capacity
                        and improving space efficiency.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedKPI === 9 && aggregateData?.Top5RoomUtilization ? (
              <>
                <div
                  className="d-flex flex-column overflow-auto justify-content-center align-items-center gap-3"
                  style={{ height: "100%" }}
                >
                  <div className="text-white" style={{ fontSize: "20px" }}>
                    Top 5 Locations
                  </div>

                  {/* Table */}
                  <div style={{ height: "80%" }}>
                    <Top5KPI data={aggregateData?.Top5RoomUtilization} />
                  </div>
                  {/* Description about*/}
                  <div className="d-flex flex-column" style={{ height: "20%" }}>
                    <div className="w-100 d-flex justify-content-around text-white">
                      <div className="d-flex text-center text-white p-2">
                        This table highlights the top occupied locations within
                        the office, showing location data number of people,
                        available seats, occupancy utilization rate, and space
                        utilization rate, enabling real-time monitoring of
                        high-traffic areas and space efficiency.
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              performanceLoading && <CircularProgress />
            )}
          </div>
        </div>
        {/* Lower Part  */}
        <div
          className="row w-100 flex-column m-0 overflow-hidden"
          style={{ backgroundColor: "#112138" }}
        >
          <div className="col flex flex-column ">
            {/* Carousel Navigation */}
            <div className="w-100 mx-auto d-flex flex-row justify-content-center align-items-center mb-2">
              {/* Title */}
              <h5 className="text-white align-self-start pt-1">
                Monitor Board
              </h5>
              {/* Navigation Tabs */}
              <Tabs
                value={selectedTabIndex}
                onChange={(e, index) => handleTabClick(index)}
                aria-label="icon tabs example"
              >
                <Tab
                  sx={{ color: "white", fontSize: "10px" }}
                  icon={<PieChartOutlinedIcon sx={{ color: "white" }} />}
                  aria-label="favorite"
                  label={"Occupancy"}
                  iconPosition="top"
                />
                <Tab
                  sx={{ color: "white", fontSize: "10px" }}
                  icon={
                    <StackedLineChartOutlinedIcon sx={{ color: "white" }} />
                  }
                  aria-label="phone"
                  label="Entry Exit"
                  iconPosition="top"
                />
                <Tab
                  disabled={userInfo?.client_id !== 1}
                  sx={{ color: "white", fontSize: "10px" }}
                  icon={
                    userInfo?.client_id === 1 ? (
                      <AddchartOutlinedIcon sx={{ color: "white" }} />
                    ) : null
                  }
                  aria-label="person"
                  label={userInfo?.client_id === 1 ? "KPI Analytics" : ""}
                  iconPosition="top"
                />
                <Tab
                  disabled={userInfo?.client_id !== 1}
                  sx={{ color: "white", fontSize: "10px" }}
                  icon={
                    userInfo?.client_id === 1 ? (
                      <RuleOutlinedIcon sx={{ color: "white" }} />
                    ) : null
                  }
                  aria-label="person"
                  label={userInfo?.client_id === 1 ? "Patterns" : ""}
                  iconPosition="top"
                />
              </Tabs>
            </div>
            {/* Carousel*/}

            <Swiper
              ref={swiperRef}
              modules={[Navigation, Pagination, Scrollbar, A11y]}
              spaceBetween={20}
              slidesPerView={1}
              navigation
              // pagination={{ clickable: true }}
              onSlideChange={() => setSelectedTabIndex(swiper.activeIndex)}
              onSwiper={(swiper) => setSwiper(swiper)}
            >
              {/* Overview Slide */}
              <SwiperSlide>
                <Swiper
                  centeredSlides={true}
                  centeredSlidesBounds={true}
                  centerInsufficientSlides={true}
                  breakpoints={breakpoints}
                  pagination={{ clickable: true }}
                  scrollbar={{ draggable: true }}
                  onSlideChange={() => console.log("nested slide change")}
                  onSwiper={(swiper) => console.log(swiper)}
                >
                  {/* No Of People by Location */}
                  <SwiperSlide>
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(1);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        {/* <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <NoOfPeopleKPI
                              avgUtil={aggregateData?.NoOfPeople}
                              maxUtil={aggregateData?.MaxNoOfPeople}
                              minUtil={aggregateData?.MinNoOfPeople}
                            />
                          </CardContent>
                        </Card> */}
                        <Card sx={cardStyle}>
                          <CardContent sx={chipStyle}>
                            Occupancy by Location(s)
                            <img
                              src="/images/icons/no-of-people.png"
                              alt=""
                              width={80}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>

                  {/* No Of Seat(s) by Location */}
                  <SwiperSlide>
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(2);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        {/* <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <NoOfEmptySeatKPI
                              avgUtil={aggregateData?.NoOfEmptySeat}
                              maxUtil={aggregateData?.MaxNoOfEmptySeat}
                              minUtil={aggregateData?.MinNoOfEmptySeat}
                            />
                          </CardContent>
                        </Card> */}

                        <Card sx={cardStyle}>
                          <CardContent sx={chipStyle}>
                            Number of Seat(s) by Location(s)
                            <img
                              src="/images/icons/no-of-seat.png"
                              alt=""
                              width={80}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>

                  {/* Occupancy Utilization -> Space Utilization */}
                  <SwiperSlide>
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setSelectedKPI(4);
                        }}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <OccupancyUtilization
                              avgUtil={aggregateData?.AvgOccupancyUtilization}
                              maxUtil={aggregateData?.MaxOccupancyUtilization}
                              minUtil={aggregateData?.MinOccupancyUtilization}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>

                  {/* Top 5 Location*/}
                  <SwiperSlide>
                    {filteredPerformance && performance ? (
                      <motion.div
                        whileHover={{
                          scale: 1.05,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                          backgroundColor: "#0b4569",
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSelectedKPI(9)}
                        className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      >
                        <Card sx={cardStyle}>
                          <CardContent sx={cardContentStyle}>
                            <div className="card-value">Top 5 Locations</div>
                            {aggregateData?.Top5RoomUtilization?.map(
                              (item, index) => (
                                <div className="w-100 d-flex align-items-center  gap-2">
                                  {index + 1 === 1 ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="30px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#FFD700"
                                    >
                                      <path
                                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Zm-20 200h80v-400H380v80h80v320Z"
                                        stroke="black"
                                        stroke-width="20"
                                      />
                                    </svg>
                                  ) : index + 1 === 2 ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="30px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#C0C0C0"
                                    >
                                      <path
                                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320ZM360-280h240v-80H440v-80h80q33 0 56.5-23.5T600-520v-80q0-33-23.5-56.5T520-680H360v80h160v80h-80q-33 0-56.5 23.5T360-440v160Z"
                                        stroke="black"
                                        stroke-width="10"
                                      />
                                    </svg>
                                  ) : index + 1 === 3 ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="30px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#CD7F32"
                                    >
                                      <path
                                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320ZM360-280h160q33 0 56.5-23.5T600-360v-60q0-26-17-43t-43-17q26 0 43-17t17-43v-60q0-33-23.5-56.5T520-680H360v80h160v80h-80v80h80v80H360v80Z"
                                        stroke="black"
                                        stroke-width="10"
                                      />
                                    </svg>
                                  ) : index + 1 === 4 ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="30px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#5f6368"
                                    >
                                      <path
                                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Zm40 200h80v-400h-80v160h-80v-160h-80v240h160v160Z"
                                        stroke="black"
                                        stroke-width="10"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="30px"
                                      viewBox="0 -960 960 960"
                                      width="24px"
                                      fill="#5f6368"
                                    >
                                      <path
                                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320ZM360-280h160q33 0 56.5-23.5T600-360v-80q0-33-23.5-56.5T520-520h-80v-80h160v-80H360v240h160v80H360v80Z"
                                        stroke="black"
                                        stroke-width="10"
                                      />
                                    </svg>
                                  )}{" "}
                                  <span className="">
                                    {item.Room ? item.Room.split(" - ")[0] : ""}
                                  </span>
                                </div>
                              )
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      <Card sx={cardStyle}>
                        <CardContent>
                          {performanceLoading && <CircularProgress />}
                        </CardContent>
                      </Card>
                    )}
                  </SwiperSlide>
                </Swiper>
              </SwiperSlide>

              {/* Entry Exit Slide */}
              <>
                <SwiperSlide>
                  <Swiper
                    centeredSlides={true}
                    centeredSlidesBounds={true}
                    centerInsufficientSlides={true}
                    breakpoints={breakpoints}
                    pagination={{ clickable: true }}
                    scrollbar={{ draggable: true }}
                    onSlideChange={() => console.log("nested slide change")}
                    onSwiper={(swiper) => console.log(swiper)}
                  >
                    {/* Entry Exit by Date and Hour */}
                    <SwiperSlide>
                      {filteredPerformance && performance ? (
                        <motion.div
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                            backgroundColor: "#0b4569",
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedKPI(5);
                          }}
                          className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                        >
                          <Card sx={cardStyle}>
                            <CardContent sx={chipStyle}>
                              Entry Exit Activity by Date and Hour
                              <img
                                src="/images/icons/entry_exit_icon.png"
                                alt=""
                                width={80}
                              />
                            </CardContent>
                          </Card>
                        </motion.div>
                      ) : (
                        <Card sx={cardStyle}>
                          <CardContent>
                            {performanceLoading && <CircularProgress />}
                          </CardContent>
                        </Card>
                      )}
                    </SwiperSlide>

                    {/* Net Entry Exit by Date and Hour */}
                    <SwiperSlide>
                      {filteredPerformance && performance ? (
                        <motion.div
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                            backgroundColor: "#0b4569",
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedKPI(7);
                          }}
                          className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                        >
                          <Card sx={cardStyle}>
                            <CardContent sx={chipStyle}>
                              Net Entry Exit By Date and Hour
                              <img
                                src="/images/icons/people-count-traffic.png"
                                alt=""
                                width={80}
                              />
                            </CardContent>
                          </Card>
                        </motion.div>
                      ) : (
                        <Card sx={cardStyle}>
                          <CardContent>
                            {performanceLoading && <CircularProgress />}
                          </CardContent>
                        </Card>
                      )}
                    </SwiperSlide>

                    {/* Avg Entry Exit */}
                    <SwiperSlide>
                      {filteredPerformance && performance ? (
                        <motion.div
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                            backgroundColor: "#0b4569",
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedKPI(20);
                          }}
                          className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                        >
                          <Card sx={cardStyle}>
                            <CardContent sx={cardContentStyle}>
                              <EntryExit
                                avgInOut={
                                  aggregateData?.TotalEntry /
                                  aggregateData?.TotalExit
                                }
                                avgIn={aggregateData?.TotalEntry}
                                avgOut={aggregateData?.TotalExit}
                              />
                            </CardContent>
                          </Card>
                        </motion.div>
                      ) : (
                        <Card sx={cardStyle}>
                          <CardContent>
                            {performanceLoading && <CircularProgress />}
                          </CardContent>
                        </Card>
                      )}
                    </SwiperSlide>
                  </Swiper>
                </SwiperSlide>
              </>

              {/* KPI Analytic Slide */}
              {userInfo?.client_id === 1 && (
                <>
                  <SwiperSlide>
                    <Swiper
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                      centerInsufficientSlides={true}
                      breakpoints={breakpoints}
                      pagination={{ clickable: true }}
                      scrollbar={{ draggable: true }}
                      onSlideChange={() => console.log("nested slide change")}
                      onSwiper={(swiper) => console.log(swiper)}
                    >
                      {/* Space Utilization /Comfort Occupancy */}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setSelectedKPI(3);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                <SpaceUtilization
                                  avgUtil={aggregateData?.AvgSpaceUtilization}
                                  maxUtil={aggregateData?.MaxSpaceUtilization}
                                  minUtil={aggregateData?.MinSpaceUtilization}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/* No of People by Date and Hour */}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setSelectedKPI(6);
                            }}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                No of People by Date and Hour
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/* Other KPIs */}
                      {userInfo?.client_id === 1 && (
                        <>
                          {/* Peak Occupancy Distribution*/}
                          <SwiperSlide>
                            {filteredPerformance && performance ? (
                              <motion.div
                                whileHover={{
                                  scale: 1.05,
                                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                  backgroundColor: "#0b4569",
                                }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setSelectedKPI(8)}
                                className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                              >
                                <Card sx={cardStyle}>
                                  <CardContent sx={cardContentStyle}>
                                    <PeakOccupancyKPI
                                      avgUtil={
                                        aggregateData
                                          ?.PeakOccupancyByHourLocation
                                          ?.avgPeakOccupancy
                                      }
                                      maxUtil={
                                        aggregateData
                                          ?.PeakOccupancyByHourLocation
                                          ?.maxPeakOccupancy
                                      }
                                      minUtil={
                                        aggregateData
                                          ?.PeakOccupancyByHourLocation
                                          ?.minPeakOccupancy
                                      }
                                    />
                                  </CardContent>
                                </Card>
                              </motion.div>
                            ) : (
                              <Card sx={cardStyle}>
                                <CardContent>
                                  {performanceLoading && <CircularProgress />}
                                </CardContent>
                              </Card>
                            )}
                          </SwiperSlide>

                          {/* People Flow*/}
                          <SwiperSlide>
                            {filteredPerformance && performance ? (
                              <motion.div
                                whileHover={{
                                  scale: 1.05,
                                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                  backgroundColor: "#0b4569",
                                }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => console.log("People Flow click")}
                                className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                              >
                                <Card sx={cardStyle}>
                                  <CardContent sx={cardContentStyle}>
                                    People Flow
                                  </CardContent>
                                </Card>
                              </motion.div>
                            ) : (
                              <Card sx={cardStyle}>
                                <CardContent>
                                  {performanceLoading && <CircularProgress />}
                                </CardContent>
                              </Card>
                            )}
                          </SwiperSlide>
                        </>
                      )}
                      {/*Occupancy Impact on Air Quality*/}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => console.log("")}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                Occupancy Impact on Air Quality
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/*Occupancy Impact on Temperature*/}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => console.log("")}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                Occupancy Impact on Temperature
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/* Occupancy Impact on Humidity*/}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => console.log("")}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                Occupancy Impact on Humidity
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/* Hide the Guest Vs People not super admin */}
                      {userInfo?.client_id === 1 && (
                        <SwiperSlide>
                          {/* Guest vs Employee*/}
                          {filteredPerformance && performance ? (
                            <motion.div
                              whileHover={{
                                scale: 1.05,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                backgroundColor: "#0b4569",
                              }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                console.log("");
                              }}
                              className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                            >
                              <Card sx={cardStyle}>
                                <CardContent sx={cardContentStyle}>
                                  Guest Vs Employee
                                </CardContent>
                              </Card>
                            </motion.div>
                          ) : (
                            <Card sx={cardStyle}>
                              <CardContent>
                                {performanceLoading && <CircularProgress />}
                              </CardContent>
                            </Card>
                          )}
                        </SwiperSlide>
                      )}
                    </Swiper>
                  </SwiperSlide>
                </>
              )}

              {/* Pattern Slide */}
              {userInfo?.client_id === 1 && (
                <>
                  <SwiperSlide>
                    <Swiper
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                      centerInsufficientSlides={true}
                      breakpoints={breakpoints}
                      pagination={{ clickable: true }}
                      scrollbar={{ draggable: true }}
                      onSlideChange={() => console.log("nested slide change")}
                      onSwiper={(swiper) => console.log(swiper)}
                    >
                      {/*Empty Seat*/}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => console.log("")}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                Empty Seat
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/*Taligating Pattern*/}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => console.log("")}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                Taligating
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>

                      {/*Navigation*/}
                      <SwiperSlide>
                        {filteredPerformance && performance ? (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                              backgroundColor: "#0b4569",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => console.log("")}
                            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                          >
                            <Card sx={cardStyle}>
                              <CardContent sx={cardContentStyle}>
                                Navigation
                              </CardContent>
                            </Card>
                          </motion.div>
                        ) : (
                          <Card sx={cardStyle}>
                            <CardContent>
                              {performanceLoading && <CircularProgress />}
                            </CardContent>
                          </Card>
                        )}
                      </SwiperSlide>
                    </Swiper>
                  </SwiperSlide>
                </>
              )}
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Performance;
